import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ColumnVisibility } from '@/components/common/ColumnVisibility/ColumnVisibility';
import { useColumnVisibility } from '@/components/common/ColumnVisibility/useColumnVisibility';
import { advanceFiltersNormalizer } from '@/helpers/advanceFilters/advanceFiltersNormalizer.helper.ts';
import { useModals } from '@/router';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import { AggregatedReport } from '../AggregatedReport';
import { CampaignPlanList } from '../CampaignPlanList';
import {
  CAMPAIGN_PLAN_COLUMN_VISIBILITY_KEY,
  CAMPAIGN_PLAN_HIDEABLE_COLUMN_IDS,
  useCampaignPlanColumnLabels
} from '../CampaignPlanList/campaignPlanColumns';
import EntityTabEmptyState from '../EntityTabEmptyState/EntityTabEmptyState';
import { ListFilters } from '../ListFilters';
import { Advertiser } from '../ListFilters/Advertiser';
import { DateRange } from '../ListFilters/DateRange';
import { Status } from '../ListFilters/Status';
import { getColumnVisibilityOptions } from '../Shared/columnVisibilityOptions';

export default function CampaignPlanTab() {
  const { applyVisibleColumns, isVisible: isColumnVisible } = useColumnVisibility(
    CAMPAIGN_PLAN_COLUMN_VISIBILITY_KEY,
    CAMPAIGN_PLAN_HIDEABLE_COLUMN_IDS
  );
  const columnLabels = useCampaignPlanColumnLabels();
  const {
    advertisers,
    cacheSeed,
    campaignListFilters,
    hasCampaigns,
    isLoadingCountLineItems,
    isLoadingGetAdvertisers,
    isLoadingGetCampaigns,
    isLoadingGetInsertionOrders,
    selectedCps,
    setCacheSeed,
    setCampaignListFilters,
    setSelectedCps
  } = useCampaignStore() as CampaignsStore;

  const isLoading =
    isLoadingCountLineItems || isLoadingGetAdvertisers || isLoadingGetCampaigns || isLoadingGetInsertionOrders;

  const { formatMessage } = useIntl();
  const modals = useModals();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const advertiserId = searchParams.get('advertiserId');
  const [drillDownResyncKey, setDrillDownResyncKey] = useState(0);
  const hasAppliedDrillDown = useRef(false);

  const internalCacheSeed = cacheSeed + JSON.stringify(campaignListFilters);
  const statusFilter = campaignListFilters.advanceFilters?.find((el: Record<string, any>) => el.id === 'status');
  const datesFilter = campaignListFilters.advanceFilters?.find((el: Record<string, any>) => el.id === 'dates');
  const advertiserFilter = campaignListFilters.advanceFilters?.find(
    (el: Record<string, any>) => el.id === 'advertiserIds'
  );
  const leftFilters = [
    {
      className: 'campaignsListsFilters__leftFiltersSelectorSmall',
      component: (
        <Advertiser advertisers={advertisers} id="campaignPlanTabAdvertiserFilter" value={advertiserFilter?.value} />
      ),
      id: 'campaignPlanFilters__advertiserFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersStatus',
      component: <Status value={statusFilter?.value} />,
      id: 'campaignPlanFilters__statusFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersDateRange',
      component: <DateRange id="tab_campaignPlan__date_filter" value={datesFilter?.value} />,
      id: 'campaignPlanFilters__dateRangeFilter',
      sendCallback: true
    }
  ];

  useEffect(() => {
    return () => {
      setSelectedCps([]);
    };
  }, []);

  useEffect(() => {
    if (hasAppliedDrillDown.current || !advertiserId) {
      return;
    }
    hasAppliedDrillDown.current = true;
    setCampaignListFilters({ advanceFilters: [{ id: 'advertiserIds', value: [advertiserId] }] });
    setDrillDownResyncKey(prevKey => prevKey + 1);
    setSearchParams(
      prevParams => {
        prevParams.delete('advertiserId');
        return prevParams;
      },
      { replace: true }
    );
  }, [advertiserId]);

  const handleFilter = (newFilters: Record<string, any>) => {
    setCampaignListFilters(newFilters);
    setCacheSeed();
  };

  const handleReset = () => {
    setCampaignListFilters({});
    setCacheSeed();
  };

  const handleListChange = () => {
    setCacheSeed();
  };

  const handleListSelectionChange = setSelectedCps;

  const normalizedFilters = useMemo(() => {
    let filters: Record<string, any> = {};
    if (campaignListFilters.text) {
      filters.name = encodeURIComponent(campaignListFilters.text);
    }
    filters = advanceFiltersNormalizer(campaignListFilters.advanceFilters, filters);
    return filters;
  }, [campaignListFilters.text, campaignListFilters.advanceFilters]);

  if (!hasCampaigns && !isLoading) {
    return (
      <EntityTabEmptyState
        buttonProps={{
          onClick: () =>
            modals.open('/campaigns/new/campaignPlanModal', {
              at: { pathname: '/campaigns', search: location.search },
              state: { campaignPlan: null, modalId: 'campaignPlan-modal' }
            }),
          text: formatMessage({
            defaultMessage: 'Create Campaign Plan',
            id: 'campaignPlans.emptyState.createButton'
          })
        }}
        description={formatMessage({
          defaultMessage: 'Create your first Campaign Plan',
          id: 'campaignPlans.emptyState.description'
        })}
        title={formatMessage({
          defaultMessage: 'Get started today!',
          id: 'campaignPlans.emptyState.title'
        })}
      />
    );
  }

  return (
    <>
      <ListFilters
        extraActions={
          <ColumnVisibility
            columns={getColumnVisibilityOptions(columnLabels, CAMPAIGN_PLAN_HIDEABLE_COLUMN_IDS, isColumnVisible)}
            onApply={applyVisibleColumns}
            prefixId="campaignPlanTab"
          />
        }
        forceResyncKey={drillDownResyncKey}
        initialFilters={campaignListFilters}
        leftFilters={leftFilters}
        onFilter={handleFilter}
        onReset={handleReset}
        prefixId="campaignPlanTab"
      />
      <AggregatedReport selectedEntities={selectedCps} />
      <CampaignPlanList
        cacheSeed={internalCacheSeed}
        filters={normalizedFilters}
        isColumnVisible={isColumnVisible}
        onListChange={handleListChange}
        onSelectionChange={handleListSelectionChange}
      />
    </>
  );
}
